import { useStore } from '@nanostores/react';
import { Trash2 } from 'lucide-react';

import { decodeLotusUuidTimestampMs } from '@/shared/lib';
import { Badge } from '@/shared/ui/shadcn/badge';
import { Button } from '@/shared/ui/shadcn/button';
import { ScrollArea } from '@/shared/ui/shadcn/scroll-area';

import { $debugEvents, clearDebugEvents } from '../model/debugEventStore';

import { DebugEventRow } from './DebugEventRow';

function eventTimestampMs(id: string): number {
  return decodeLotusUuidTimestampMs(id) ?? 0;
}

function formatRelativeTime(ts: number, referenceTs: number): string {
  const deltaMs = ts - referenceTs;
  if (Math.abs(deltaMs) < 1000) {
    return `${deltaMs >= 0 ? '+' : ''}${deltaMs}ms`;
  }
  const deltaSec = deltaMs / 1000;
  return `${deltaSec >= 0 ? '+' : ''}${deltaSec.toFixed(1)}s`;
}

export function DebugPanel() {
  const events = useStore($debugEvents);

  const lastEventId = events[events.length - 1]?.id;
  const referenceTs = lastEventId !== undefined ? eventTimestampMs(lastEventId) : 0;

  return (
    <div className="flex h-full flex-col">
      {/* Toolbar */}
      <div className="flex items-center gap-1.5 border-b border-border px-3 py-1.5">
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={clearDebugEvents}
          aria-label="Clear all events"
        >
          <Trash2 className="size-3.5" />
        </Button>

        <div className="flex-1" />

        <Badge variant="secondary" className="text-[10px]">
          {events.length}
        </Badge>
      </div>

      {/* Timeline */}
      <ScrollArea className="flex-1">
        {events.length === 0 ? (
          <div className="flex h-32 items-center justify-center text-xs text-muted-foreground">
            No debug events captured yet
          </div>
        ) : (
          events.map((ev) => (
            <DebugEventRow
              key={ev.id}
              event={ev}
              relativeLabel={formatRelativeTime(eventTimestampMs(ev.id), referenceTs)}
            />
          ))
        )}
      </ScrollArea>
    </div>
  );
}
